import { useRef, useState, useEffect, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Wrapper, TextWrapperSpan, FlexRow, CPButton } from "../../../constants/common/StyledGlobalConstants";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { updateLocaleOptions } from "primereact/api";
import ScheduleIcon from "../../../assets/icons/ic_scheduler.png";
import { StarSpan } from "../VehicleorAssetDetails/CamerasAndOtherAccessories";
import moment from "moment";
import * as API from "../../../api/schedulingPortal";
import * as Actions from "../../../store/actions/schedulingportal/technicianAppointment";
import { getPageLabel, getPendoId } from "../../../util/cfstPendoUtils";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";
import { CFST_PENDO_DEFAULT_LABEL, pendoTrack } from "../../../util/pendoUtil";
import { setPreferredSelectedDate } from "../../../store/actions/global/global";
import PropTypes from "prop-types";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;
const Wrappers = styled.div`
  position: relative;
  width: 100%;
`;
//to set the day names in the calendar
const StyledInputWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 77%;
  border: 1px solid #d8dada;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 1rem;
  background-color: #fff;

  input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 1rem;
    background: transparent;
    cursor: text; /* Indicate the input is editable */
  }

  img {
    width: 16px;
    height: 16px;
    margin-left: auto;
    cursor: pointer;
  }
`;
updateLocaleOptions(
  {
    dayNamesMin: ["S", "M", "T", "W", "T", "F", "S"],
  },
  "en"
);
const getNext60DatesExcludingSundays = () => {
  const dates = [];
  let date = 60;
  const twoDaysAfterToday = moment().startOf("day").add(2, "days").toDate();
  for (let i = 0; i < date; i++) {
    const nextDate = moment(twoDaysAfterToday).add(i, "days").toDate();
    dates.push(nextDate);
  }
  return dates;
};
const CustomDateInput = forwardRef(function CustomDateInput({ value, onClick, onChange, placeholder }, ref) {
  return (
    <StyledInputWrapper>
      <input
        id={getPendoId("calender-date-picker-input")}
        ref={ref}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onClick={onClick}
        aria-label="Preferred date"
        style={{
          fontFamily: '"Neue Haas Grotesk Text Std"',
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "0%",
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
      />
      <button
        type="button"
        tabIndex={0}
        aria-label="Open calendar"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          marginLeft: "auto",
          cursor: "pointer",
          display: "flex",
          alignItems: "center"
        }}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <img src={ScheduleIcon} alt="" style={{ width: "16px", height: "16px" }} />
      </button>
    </StyledInputWrapper>
  );
});

CustomDateInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

const PreferredAppointmentDate = ({
  setPreferredDate,
  setVendorAvailableSlots,
  setApptTimeDateAndTime,
  showProgressBar,
  setShowProgressBar,
}) => {
  const { isReschedule, jobDetails, jobResponseData, accountDetails, contactFormValues } = useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const enabledDates = getNext60DatesExcludingSundays();
  const [selectedDate, setSelectedDate] = useState(null);
  const calendarRef = useRef(null);

  const { windowSize, erpBusinessPartner, accountNumber } = useSelector((state) => state.ACCOUNT);
  const dispatch = useDispatch();
  const isTablet = windowSize.width < DESKTOP;
  const isMobile = windowSize.width < TABLET;

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.updateViewDate(null, selectedDate || new Date());
    }
    dispatch(setPreferredSelectedDate(selectedDate));
  }, [selectedDate]);

  const applyHandler = async () => {
    setPreferredDate(selectedDate);
    dispatch(Actions.setPreferredDate(selectedDate));
    setVendorAvailableSlots(null);
    setApptTimeDateAndTime(null);
    setShowProgressBar(true);
    const slotsData = await fetchSlots(selectedDate);
    setVendorAvailableSlots(slotsData);
    setShowProgressBar(false);
  };

  const isDateDisabled = (date) => {
    return enabledDates && !enabledDates.some((enabledDate) => enabledDate.toDateString() === date.toDateString());
  };

  const fetchSlots = async (selectedDate) => {
    let payload = {},
      id = "";
    if (isReschedule) {
      id = jobDetails?.id;
      let name = jobDetails?.name?.split(" ");
      payload = {
        Header: { requestControlId: "vzc_" + Date.now() },
        Body: {
          header: { clientAppName: "VZC", clientAppUserName: "ECOM", internationalFlag: accountDetails?.data?.vpmInternational || false },
          vendorId: jobDetails?.vendorId ? jobDetails?.vendorId : "",
          addressLine1: jobDetails?.address1 || "",
          addressLine2: jobDetails?.address2 || "",
          city: jobDetails?.city || "",
          state: jobDetails?.state || "",
          zipCode: jobDetails?.zipCode || "",
          serviceRequestType: "Support/De-Re",
          startingDate: moment(selectedDate).format("YYYYMMDD"),
          customerType: accountDetails?.data?.vzcSegement || "",
          firstName: name[0] || "",
          lastName: name[1] || "",
          accountName: erpBusinessPartner?.data?.companyName ?? "",
          jobDuration: jobDetails?.jobDuration || 0,
          serviceJobId: jobDetails?.serviceJobId || "",
          billingCountry: accountDetails?.data?.billingCountry ?? "",
        },
      };
    } else {
      id = jobResponseData?.id ?? "";
      let { firstName, lastName } =
        contactFormValues.selectedContactType === "use-existing-contact" ? contactFormValues.existingContact : contactFormValues.newContact;
      payload = {
        Header: { requestControlId: "vzc_" + Date.now() },
        Body: {
          header: { clientAppName: "VZC", clientAppUserName: "ECOM", internationalFlag: accountDetails?.data?.vpmInternational || false },
          vendorId: null,
          addressLine1: jobResponseData?.address1 || "",
          addressLine2: jobResponseData?.address2 || "",
          city: jobResponseData?.city || "",
          state: jobResponseData?.stateCode || "",
          zipCode: jobResponseData?.zipCode || "",
          serviceRequestType: "Support/De-Re",
          startingDate: moment(selectedDate).format("YYYYMMDD"),
          customerType: jobResponseData?.vztSegment || "",
          firstName: firstName || "",
          lastName: lastName || "",
          accountName: jobResponseData?.customerName || "",
          jobDuration: jobResponseData?.estAppointmentLength || 0,
          serviceJobId: "",
          billingCountry: accountDetails?.data?.billingCountry ?? "",
        },
      };
    }

    // If vpmInternational is true, compute and add isRoundTrip based on distanceDescription from CSP details
    if (accountDetails?.data?.vpmInternational) {
      const distanceDescription = accountDetails?.data?.distanceDescription || "";
      const distanceCalculationType = distanceDescription === "Mileage - Roundtrip" ? "roundtrip" : "oneway";
      payload.Body.isRoundTrip = distanceCalculationType === "roundtrip" ? "Y" : "N";
    }

    try {
      let response = await API.fetchSlots(payload, id);
      if (response.data.statusCode === "200") {
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | ${getPageLabel()} | Slots api`, {
          status: "Success",
          statusCode: response.data.statusCode,
          accountId: accountNumber,
        });
        dispatch(Actions.setAPIFailure({ isJobFailure: false, error: "", isSlotsFailure: false, isTransactionFailure: false }));
        return response.data?.data;
      } else {
        // set error and return null
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | ${getPageLabel()} | Slots api`, {
          status: "Null response",
          statusCode: response.data.statusCode,
          accountId: accountNumber,
        });
        dispatch(
          Actions.setAPIFailure({
            isJobFailure: false,
            error: "API failed to retrieve date and time slots",
            isSlotsFailure: false,
            isTransactionFailure: true,
          })
        );
        return null;
      }
    } catch (err) {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | ${getPageLabel()} | Slots api`, {
        status: "Failed",
        statusCode: err.response?.data?.statusCode || err.data?.statusCode || err.response?.status,
        accountId: accountNumber,
      });
      dispatch(
        Actions.setAPIFailure({
          isJobFailure: false,
          error: "API failed to retrieve date and time slots",
          isSlotsFailure: false,
          isTransactionFailure: true,
        })
      );
      return null;
    }
  };

  return (
    <FlexRow
      marginTop={isTablet ? "1.5rem" : "2rem"}
      padding={isMobile ? "1rem" : "1.5rem"}
      background="#F6F6F6"
      borderRadius="0.5rem"
      id="date-selection-block"
    >
      <FlexRow width={isMobile ? "100%" : "19.68rem"}>
        <TextWrapperSpan fontSize="0.75rem" fontWeight="700" lineHeight="0.75rem" marginBottom="0.5rem !important">
          Preferred date <StarSpan>*</StarSpan>
        </TextWrapperSpan>
        <Wrappers>
          <DatePicker
            id={getPendoId("calender-date-picker-icon")}
            tabIndex={0}
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            minDate={enabledDates[0]}
            maxDate={enabledDates[enabledDates.length - 1]}
            filterDate={(date) => !isDateDisabled(date)}
            placeholderText="mm/dd/yyyy"
            dateFormat="MM/dd/yyyy"
            showPopperArrow={false}
            dropdownMode="select"
            customInput={<CustomDateInput />}
          />
        </Wrappers>
        <TextWrapperSpan fontSize="0.75rem" fontWeight="400" lineHeight="1.125rem" color="#707070" marginTop="4px !important">
          {isReschedule ? `The date must be within 60 days of today.` : ` Appointments can be made up to 60 days in advance.`}
        </TextWrapperSpan>
      </FlexRow>
      <Wrapper
        alignContent="center"
        marginTop={isMobile && "1.5rem"}
        marginLeft={!isMobile && "-3rem"}
        marginBottom={!isMobile && "2px"}
        width={isMobile ? "100%" : "4.94rem"}
      >
        <CPButton
          id={getPendoId("calender-apply-button")}
          role="button"
          secondary
          disabled={!selectedDate || showProgressBar}
          fontSize="0.875rem"
          lineHeight="1rem"
          width={isMobile ? "100%" : "auto"}
          boxButton
          aria-label="apply"
          background="#fff"
          onClick={applyHandler}
        >
          APPLY
        </CPButton>
      </Wrapper>
    </FlexRow>
  );
};

PreferredAppointmentDate.propTypes = {
  setPreferredDate: PropTypes.func.isRequired,
  setVendorAvailableSlots: PropTypes.func.isRequired,
  setApptTimeDateAndTime: PropTypes.func.isRequired,
  showProgressBar: PropTypes.bool.isRequired,
  setShowProgressBar: PropTypes.func.isRequired,
};

export default PreferredAppointmentDate;